import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../../api/axios";
import endpoints from "../../api/endpoints";

interface MyTeamsUBTList {
  [key: string]: any;
}

interface MyTeamsUBTState {
  myTeams: MyTeamsUBTList[];
  loading: boolean;
  loadingList?: boolean;
  error: string | null;
  currentPage: number;
  perPage: number;
  totalItems: number;
  totalPages: number;
  totalCount: number; // Add separate field for count API response
}

const initialState: MyTeamsUBTState = {
  myTeams: [],
  loading: false,
  loadingList: false,
  error: null,
  currentPage: 1,
  perPage: 20,
  totalItems: 0,
  totalPages: 0,
  totalCount: 0, // Initialize the count field
};

// Helper function to handle API errors
const handleApiError = (error: any, rejectWithValue: any, defaultMessage: string) => {
  console.error('API error:', error);

  if (
    error?.message === "canceled" ||
    error?.message === "Request cancelled" ||
    error?.code === "ECONNABORTED"
  ) {
    return rejectWithValue("Request cancelled");
  }

  const errorMessage =
    error?.response?.data?.message ??
    error?.response?.data?.error ??
    error?.response?.data?.detail ??
    error?.response?.statusText ??
    error?.message ??
    defaultMessage;

  return rejectWithValue(errorMessage);
};

export const fetchMyTeamsUBTList = createAsyncThunk(
  "ubtPortal/fetchMyTeamsUBTList",
  async (
    {
      page,
      perPage,
      sortColumn = 'TEAMID',
      sortOrder = 'ASC',
      countOnly = false
    }: {
      page: number;
      perPage: number;
      sortColumn?: string;
      sortOrder?: 'ASC' | 'DESC';
      countOnly?: boolean;
    },
    { rejectWithValue, signal },
  ) => {
    try {
      const options: any = {
        signal,
        params: {
          sortColumn,
          sortOrder,
          page,
          per_page: perPage,
          countOnly,
        },
      };

      const response = await api.get(
        endpoints.ubt.myTeams.myTeamsList,
        options,
      );
      return response.data;
    } catch (error: any) {
      console.error('fetchMyTeamsUBTList - API error:', error);
      return handleApiError(error, rejectWithValue, "Failed to fetch manager applications");
    }
  },
);

export const fetchMyTeamsCount = createAsyncThunk(
  "ubtPortal/fetchMyTeamsCount",
  async (_, { rejectWithValue, signal }) => {
    try {
      const options: any = {
        signal,
        params: {
          sortColumn: 'TEAMID',
          sortOrder: 'ASC',
          page: 1,
          per_page: 10,
          countOnly: true,
        },
      };

      const response = await api.get(
        endpoints.ubt.myTeams.myTeamsList,
        options,
      );
      return response.data;
    } catch (error: any) {
      return handleApiError(error, rejectWithValue, "Failed to fetch teams count");
    }
  },
);

// Async thunk to delete a team response
export const deleteMyTeam = createAsyncThunk<
  number,
  number,
  { rejectValue: string }
>("ubtPortal/deleteTeam", async (id, { rejectWithValue }) => {
  try {
    await api.delete(
      endpoints.ubt.myTeams.deleteTeam(id)
    );
    return id;
  } catch (error: any) {
    return rejectWithValue(error.response?.data ?? `An error occurred while deleting team with ID ${id}`);
  }
});


const myTeamsSlice = createSlice({
  name: "myTeams",
  initialState,
  reducers: {
    resetMyTeamsUBTState: (state) => {
      state.myTeams = [];
      state.loading = false;
      state.loadingList = false;
      state.error = null;
      state.currentPage = 1;
      state.totalItems = 0;
      state.totalPages = 0;
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchMyTeamsUBTList.pending, (state) => {
      state.loading = true;
      state.loadingList = true;
      state.error = null;
    });
    builder.addCase(fetchMyTeamsUBTList.fulfilled, (state, action) => {
      state.loading = false;
      state.loadingList = false;
      const payload: any = action.payload;

      // Handle API response with items array and meta object
      if (payload?.items && Array.isArray(payload.items)) {
        // Group items by teamId while preserving the order from API
        const groupedTeamsMap = new Map<number, any>();
        const teamOrder: number[] = []; // Track the order teams appear in the sorted response

        payload.items.forEach((item: any) => {
          const teamId = item.teamId;

          if (!groupedTeamsMap.has(teamId)) {
            teamOrder.push(teamId); // Record the first occurrence order
            groupedTeamsMap.set(teamId, {
              id: teamId,
              ubtName: item.teamName,
              nuid: item.nuid,
              isDelete: item.trackingFlag, // Set based on your business logic
              details: []
            });
          }

          // Add detail for this team
          groupedTeamsMap.get(teamId).details.push({
            teamRating: item.teamRating ?? "",
            assesmentDate: item.learningPlanRefreshDt ?? "",
            entity: item.company ?? "",
            area: item.kpAreaDesc ?? "",
            location: item.glLocNm ?? "",
            costCenter: item.costCenter ?? "",
            shift: item.shiftName ?? item.shift ?? "",
          });
        });

        // Convert to array in the order teams appeared in the API response
        state.myTeams = teamOrder.map(teamId => groupedTeamsMap.get(teamId));
        state.totalItems = payload.meta?.total ?? 0;
        state.totalPages = payload.meta?.totalPages ?? 1;
        state.currentPage = payload.meta?.pageNumber ?? 1;
        state.perPage = payload.meta?.pageSize ?? 10;
      } else {
        state.myTeams = [];
      }
    });
    builder.addCase(fetchMyTeamsUBTList.rejected, (state, action) => {
      state.loading = false;
      state.loadingList = false;
      state.error = action.payload as string;
    });

    // Handle fetchMyTeamsCount
    builder.addCase(fetchMyTeamsCount.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(fetchMyTeamsCount.fulfilled, (state, action) => {
      state.loading = state.loadingList ?? false;
      const payload: any = action.payload;
      if (payload?.count !== undefined) {
        state.totalCount = payload.count;
      }
    });
    builder.addCase(fetchMyTeamsCount.rejected, (state, action) => {
      state.loading = state.loadingList ?? false;
      state.error = action.payload as string;
    });
    builder.addCase(deleteMyTeam.pending, (state) => {
        state.loading = true;
        state.error = null;
    });
    builder.addCase(deleteMyTeam.fulfilled, (state, action: PayloadAction<number>) => {
          state.loading = false;
          state.myTeams = state.myTeams.filter(
            (team) => team.id !== action.payload
       );
    });
    builder.addCase(deleteMyTeam.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload ?? action.error?.message ?? null;
    });
  },
});

export const { resetMyTeamsUBTState, setCurrentPage } = myTeamsSlice.actions;
export default myTeamsSlice.reducer;
